import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Paper,
  AlertTitle,
  Alert,
  TextField,
  Autocomplete,
} from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from "recharts";

import { useFirebaseAuth } from "../hooks/useFirebaseAuth";
import { useQuery } from "@tanstack/react-query";
import { FuelReportsService } from "../services/fuelReportsService";
import axios from "axios";
import { Loading } from "../components/Loading";
import { getValidToken } from "../utils/authUtils";
import {
  formatCurrency,
  formatText,
  formatDateRange,
  formatToLocalYMD,
} from "../utils/utils";

/** Fuel data on this dashboard is for calendar year 2025; presets and API ranges use this anchor. */
const LARGE_FLEET_DATA_YEAR = 2025;

const MONTH_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

type LargeFleetFuelDateFilter =
  | `month-${number}-${string}`
  | `quarter-${number}-${number}`;

function monthRange(year: number, monthIndex0: number): { start: Date; end: Date } {
  const start = new Date(year, monthIndex0, 1);
  start.setHours(0, 0, 0, 0);
  const end = new Date(year, monthIndex0 + 1, 0);
  end.setHours(23, 59, 59, 999);
  return { start, end };
}

function quarterRange(
  year: number,
  quarter: 1 | 2 | 3 | 4
): { start: Date; end: Date } {
  const startMonth = (quarter - 1) * 3;
  const start = new Date(year, startMonth, 1);
  start.setHours(0, 0, 0, 0);
  const end = new Date(year, startMonth + 3, 0);
  end.setHours(23, 59, 59, 999);
  return { start, end };
}

function getLargeFleetDateRangeForFilter(
  reportRange: LargeFleetFuelDateFilter
): { start: Date; end: Date } {
  const monthMatch = reportRange.match(/^month-(\d{4})-(\d{2})$/);
  if (monthMatch) {
    const y = parseInt(monthMatch[1], 10);
    const m = parseInt(monthMatch[2], 10) - 1;
    if (m >= 0 && m <= 11) return monthRange(y, m);
  }
  const quarterMatch = reportRange.match(/^quarter-(\d{4})-([1-4])$/);
  if (quarterMatch) {
    const y = parseInt(quarterMatch[1], 10);
    const q = parseInt(quarterMatch[2], 10) as 1 | 2 | 3 | 4;
    return quarterRange(y, q);
  }
  return monthRange(LARGE_FLEET_DATA_YEAR, 0);
}

interface DashboardStats {
  company: {
    name: string;
    totalVehicles: number;
    totalStaff: number;
  };
  vehicleStatus: {
    [key: string]: number;
  };
  staffStatus: {
    [key: string]: number;
  };
}

interface SuperProjectStats {
  totalIncome: number;
  totalExpectedIncome: number;
  totalExpense: number;
  totalFleet: number;
  totalFuelCost: number;
  totalMaintenanceCost: number;
  contractSummary: {
    totalSuperProjectsCount: number;
    totalProjectsCount: number;
  };
}

const LargeFleetDashboard = () => {
  const { user, loading: authLoading } = useFirebaseAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [superProjectStats, setSuperProjectStats] =
    useState<SuperProjectStats | null>(null);

  // Fuel filter states
  const [selectedTableName, setSelectedTableName] = useState<string>(
    "bihar_fuel_transactions"
  );
  const [dateFilter, setDateFilter] = useState<LargeFleetFuelDateFilter>(
    `month-${LARGE_FLEET_DATA_YEAR}-01`
  );
  const fetchDashboardStats = async () => {
    try {
      const token = await getValidToken();
      const url = `${process.env.REACT_APP_API_HOST}dashboard/stats`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data?.isSuccess) {
        setStats(response.data.data);
      }
    } catch (error) {
      return;
    } finally {
      setIsLoading(false);
    }
  };
  const fetchSuperProjectStats = async () => {
    try {
      const token = await getValidToken();
      const url = `${process.env.REACT_APP_API_HOST}project-module/super-projects/dashboard/income-summary`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setSuperProjectStats(response.data.data);
    } catch (error) {
      return;
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch vehicle consumption data
  const {
    data: reportData,
    isLoading: vehicleConsumptionLoading,
    refetch,
  } = useQuery({
    queryKey: ["vehicle-consumption-report", selectedTableName, dateFilter],
    queryFn: () => {
      const { start, end } = getLargeFleetDateRangeForFilter(dateFilter);
      const filters: Record<string, string> = {
        dateFilter,
        startDate: formatToLocalYMD(start),
        endDate: formatToLocalYMD(end),
      };

      if (selectedTableName !== "all") {
        filters.tablename = selectedTableName;
      }

      return FuelReportsService.getFuelConsumptionDashboard(filters);
    },
  });

  useEffect(() => {
    if (!authLoading && user) {
      fetchDashboardStats();
      fetchSuperProjectStats();
    }
  }, [user, authLoading]);

  if (authLoading || isLoading || vehicleConsumptionLoading) {
    return <Loading message="Loading dashboard..." fullScreen={true} />;
  }

  if (!stats) {
    return null;
  }

  // Fuel Dashboard Components
  const renderFuelSummaryCards = () => {
    if (!reportData?.data?.data) return null;
    const data = reportData.data.data as any; // Cast to any to handle different response structure

    // Check if essential data is missing
    if (
      !data.totalFuelExpense &&
      !data.totalFuelLiters &&
      !data.totalTransactions
    ) {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Paper
              sx={{
                p: 2,
                border: "1px solid #e1e4e8",
                borderRadius: 2,

                textAlign: "center",
              }}
            >
              <Typography
                variant="h6"
                sx={{ fontSize: "1rem", mb: 1, color: "#586069" }}
              >
                No Available
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontSize: "0.85rem", color: "#586069" }}
              >
                No fuel consumption data found for the selected filters.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      );
    }

    return (
      <Grid container item md={12} xs={12} spacing={2}>
        {/* Total Fuel Expense */}
        <Grid item xs={6} md={3}>
          <Paper
            sx={{
              p: 1.5,
              border: "1px solid #e1e4e8",
              borderRadius: 2,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.75rem",
                color: "#586069",
                mb: 0.5,
              }}
            >
              Total Fuel Expense
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontSize: "1.2rem",
                fontWeight: 600,
                color: "#d73527",
                mb: 0.5,
              }}
            >
              {data.totalFuelExpense
                ? formatCurrency(data.totalFuelExpense)
                : "₹0.00"}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontSize: "0.65rem",
                color: "#586069",
              }}
            >
              {data.totalTransactions || 0} transactions
            </Typography>
          </Paper>
        </Grid>
        {/* 22182314.79 */}
        {/* option.label */}
        {/* only show when bihar is selected */}
        {selectedTableName === "bihar_fuel_transactions" && (
          <Grid item xs={6} md={3}>
            <Paper
              sx={{
                p: 1.5,
                border: "1px solid #e1e4e8",
                borderRadius: 2,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.75rem",
                  color: "#586069",
                  mb: 0.5,
                }}
              >
                Total Maintenance Expense
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  fontSize: "1.2rem",
                  fontWeight: 600,
                  color: "#d73527",
                  mb: 0.5,
                }}
              >
                {formatCurrency(22182314.79)}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.65rem",
                  color: "#586069",
                }}
              >
                {3220} transactions
              </Typography>
            </Paper>
          </Grid>
        )}
        {selectedTableName !== "bihar_fuel_transactions" && (
          <Grid item xs={6} md={3}>
            <Paper
              sx={{
                p: 1.5,
                border: "1px solid #e1e4e8",
                borderRadius: 2,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.75rem",
                  color: "#586069",
                  mb: 0.5,
                }}
              >
                Total Maintenance Expense
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  color: "#d73527",
                  mb: 0.5,
                }}
              >
                {"No data"}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.65rem",
                  color: "#586069",
                }}
              >
                N/A
              </Typography>
            </Paper>
          </Grid>
        )}



        {/* Total Fuel Liters */}
        <Grid item xs={6} md={3}>
          <Paper
            sx={{
              p: 1.5,
              border: "1px solid #e1e4e8",
              borderRadius: 2,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.75rem",
                color: "#586069",
                mb: 0.5,
              }}
            >
              Total Fuel Liters
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontSize: "1.2rem",
                fontWeight: 600,
                color: "#0366d6",
                mb: 0.5,
              }}
            >
              {data.totalFuelLiters ? data.totalFuelLiters.toFixed(1) : "0.0"} L
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontSize: "0.65rem",
                color: "#586069",
              }}
            >
              Consumed
            </Typography>
          </Paper>
        </Grid>

        {/* Average Price Per Liter */}
        <Grid item xs={6} md={3}>
          <Paper
            sx={{
              p: 1.5,
              border: "1px solid #e1e4e8",
              borderRadius: 2,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.75rem",
                color: "#586069",
                mb: 0.5,
              }}
            >
              Avg Price/Liter
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontSize: "1.2rem",
                fontWeight: 600,
                color: "#f57c00",
                mb: 0.5,
              }}
            >
              ₹
              {data.averagePricePerLiter
                ? data.averagePricePerLiter.toFixed(2)
                : "0.00"}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontSize: "0.65rem",
                color: "#586069",
              }}
            >
              Per liter
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    );
  };

  const renderTopFuelStationsChart = () => {
    if (!(reportData?.data?.data as any)?.topFuelStations) return null;
    const stations = (reportData?.data?.data as any).topFuelStations.slice(
      0,
      6
    );

    const COLORS = [
      "#0088FE",
      "#00C49F",
      "#FFBB28",
      "#FF8042",
      "#8884D8",
      "#82CA9D",
    ];

    // Check if stations array is empty or has no data
    if (!stations || stations.length === 0) {
      return (
        <Paper
          sx={{ p: 2, mb: 2, border: "1px solid #e0e0e0", borderRadius: 2 }}
        >
          <Typography
            variant="body2"
            sx={{
              mb: 2,
              fontSize: "0.9rem",
              fontWeight: 600,
              color: "#24292e",
            }}
          >
            Top Fuel Stations by Amount
          </Typography>
          <Box
            sx={{
              height: 300,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              color: "#586069",
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontSize: "1rem", mb: 1, color: "#586069" }}
            >
              No Data Available
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontSize: "0.85rem", textAlign: "center" }}
            >
              No fuel station data found for the selected filters.
              <br />
              Try adjusting your date range or data source.
            </Typography>
          </Box>
        </Paper>
      );
    }

    return (
      <Paper sx={{ p: 2, mb: 2, border: "1px solid #e0e0e0", borderRadius: 2 }}>
        <Typography
          variant="body2"
          sx={{ mb: 2, fontSize: "0.9rem", fontWeight: 600, color: "#24292e" }}
        >
          Top Fuel Stations by Amount
        </Typography>
        <Box sx={{ height: 300, display: "flex", alignItems: "center" }}>
          <Box sx={{ flex: 1, height: "100%" }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={stations}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  innerRadius={40}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="totalAmount"
                  paddingAngle={2}
                >
                  {stations.map((entry: any, index: number) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: any) => [formatCurrency(value), "Amount"]}
                  labelStyle={{ fontSize: "14px", fontWeight: "bold" }}
                  contentStyle={{
                    fontSize: "12px",
                    backgroundColor: "#fff",
                    border: "1px solid #ccc",
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </Box>

          {/* Custom Legend */}
          <Box sx={{ ml: 2, minWidth: "140px" }}>
            {stations.map((entry: any, index: number) => {
              const total = stations.reduce(
                (sum: number, station: any) => sum + station.totalAmount,
                0
              );
              const percentage = ((entry.totalAmount / total) * 100).toFixed(0);

              return (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: 1.5,
                    fontSize: "0.75rem",
                  }}
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      backgroundColor: COLORS[index % COLORS.length],
                      borderRadius: "2px",
                      mr: 1,
                      flexShrink: 0,
                    }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 500,
                        color: "#24292e",
                        lineHeight: 1.2,
                        mb: 0.2,
                      }}
                    >
                      {entry.stationName.length > 15
                        ? `${entry.stationName.substring(0, 15)}...`
                        : entry.stationName}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "0.65rem",
                        color: "#586069",
                        display: "block",
                      }}
                    >
                      {percentage}% • {formatCurrency(entry.totalAmount)}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Paper>
    );
  };

  const renderVehicleWiseConsumption = () => {
    if (!(reportData?.data?.data as any)?.vehicleWiseSummary) return null;
    const vehicles = (reportData?.data?.data as any).vehicleWiseSummary.slice(
      0,
      8
    );

    // Check if vehicles array is empty or has no data
    if (!vehicles || vehicles.length === 0) {
      return (
        <Paper
          sx={{ p: 2, mb: 2, border: "1px solid #e0e0e0", borderRadius: 2 }}
        >
          <Typography
            variant="body2"
            sx={{
              mb: 2,
              fontSize: "0.9rem",
              fontWeight: 600,
              color: "#24292e",
            }}
          >
            Vehicle-wise Fuel Consumption
          </Typography>
          <Box
            sx={{
              height: 300,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              color: "#586069",
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontSize: "1rem", mb: 1, color: "#586069" }}
            >
              No Data Available
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontSize: "0.85rem", textAlign: "center" }}
            >
              No vehicle consumption data found for the selected filters.
              <br />
              Try adjusting your date range or data source.
            </Typography>
          </Box>
        </Paper>
      );
    }

    return (
      <Paper sx={{ p: 2, mb: 2, border: "1px solid #e0e0e0", borderRadius: 2 }}>
        <Typography
          variant="body2"
          sx={{ mb: 2, fontSize: "0.9rem", fontWeight: 600, color: "#24292e" }}
        >
          Vehicle-wise Fuel Consumption
        </Typography>
        <Box sx={{ height: 300 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={vehicles}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              maxBarSize={40}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="vehicleId"
                fontSize={11}
                angle={-45}
                textAnchor="end"
                height={60}
                tick={{ fill: "#666", fontSize: 11 }}
              />
              <YAxis
                fontSize={11}
                tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}K`}
                tick={{ fill: "#666", fontSize: 11 }}
              />
              <Tooltip
                formatter={(value: any, name: any) => [
                  formatCurrency(value),
                  name === "totalAmount" ? "Fuel Cost" : name,
                ]}
                labelStyle={{ fontSize: "14px", fontWeight: "bold" }}
                contentStyle={{
                  fontSize: "12px",
                  backgroundColor: "#fff",
                  border: "1px solid #ccc",
                }}
              />
              <Bar dataKey="totalAmount" name="Fuel Cost" fill="#ff6b35" />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Paper>
    );
  };

  const renderFilterAndDateRange = () => {
    const tableOptions = [
      { value: "all", label: "All Contract" },
      { value: "bihar_fuel_transactions", label: "Bihar Fuel Transactions" },
      { value: "up_als_fuel_data", label: "UP ALS" },
      {
        value: "up_mvu_fuel_data_correct",
        label: "UP MVU",
      },
      { value: "punjab_fuel_data", label: "Punjab" },
      { value: "nhai_fuel_data", label: "NHAI" },
      {
        value: "mahaprayan_odisha_fuel_data",
        label: "Mahaprayan Odisha",
      },
    ];

    const monthDateOptions: { value: LargeFleetFuelDateFilter; label: string }[] =
      MONTH_SHORT.map((name, i) => {
        const mm = String(i + 1).padStart(2, "0");
        const value = `month-${LARGE_FLEET_DATA_YEAR}-${mm}` as const;
        const { start, end } = getLargeFleetDateRangeForFilter(value);
        return {
          value,
          label: `${name} ${LARGE_FLEET_DATA_YEAR} ${formatDateRange(start, end)}`,
        };
      });

    const quarterDateOptions: {
      value: LargeFleetFuelDateFilter;
      label: string;
    }[] = ([1, 2, 3, 4] as const).map((q) => {
      const value = `quarter-${LARGE_FLEET_DATA_YEAR}-${q}` as const;
      const { start, end } = getLargeFleetDateRangeForFilter(value);
      return {
        value,
        label: `Q${q} ${LARGE_FLEET_DATA_YEAR} ${formatDateRange(start, end)}`,
      };
    });

    const periodOptionsWithGroups: {
      value: LargeFleetFuelDateFilter;
      label: string;
      group: string;
    }[] = [
      ...monthDateOptions.map((o) => ({
        ...o,
        group: `Month on month (${LARGE_FLEET_DATA_YEAR})`,
      })),
      ...quarterDateOptions.map((o) => ({
        ...o,
        group: `Quarter on quarter (${LARGE_FLEET_DATA_YEAR})`,
      })),
    ];

    const selectedContract =
      tableOptions.find((t) => t.value === selectedTableName) ?? tableOptions[0];
    const selectedPeriodOption =
      periodOptionsWithGroups.find((p) => p.value === dateFilter) ??
      periodOptionsWithGroups[0];

    return (
      <Paper sx={{ p: 1.5, borderRadius: 2 }}>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              size="small"
              options={tableOptions}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(a, b) => a.value === b.value}
              value={selectedContract}
              onChange={(_, opt) => {
                if (opt) setSelectedTableName(opt.value);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Contract"
                  sx={{ "& .MuiInputBase-input": { fontSize: "0.75rem" } }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Typography sx={{ fontSize: "0.75rem" }}>
                    {option.label}
                  </Typography>
                </li>
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={5}>
            <Autocomplete
              size="small"
              options={periodOptionsWithGroups}
              groupBy={(opt) => opt.group}
              getOptionLabel={(opt) => opt.label}
              isOptionEqualToValue={(a, b) => a.value === b.value}
              value={selectedPeriodOption}
              onChange={(_, opt) => {
                if (opt) setDateFilter(opt.value);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Period"
                  sx={{ "& .MuiInputBase-input": { fontSize: "0.75rem" } }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Typography sx={{ fontSize: "0.75rem" }}>
                    {option.label}
                  </Typography>
                </li>
              )}
            />
          </Grid>
        </Grid>
      </Paper>
    );
  };

  return (
    <Container maxWidth={false} sx={{ py: 2 }}>
      <Grid container spacing={2} sx={{ py: 1, px: 0 }}>
        {/* left column for fleet overview, staff overview, contract summary, financial summary */}
        <Grid container item xs={12} md={3} spacing={2} sx={{ height: "100%" }}>
          {/* Fleet Overview Card */}
          <Grid item xs={12}>
            <Paper
              sx={{
                p: 1.5,
                border: "1px solid #e1e4e8",
                borderRadius: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  mb: 1,
                  color: "#24292e",
                }}
              >
                Fleet Overview
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 0.8,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.75rem",
                    color: "#586069",
                    fontWeight: 500,
                  }}
                >
                  Total Vehicles
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: "1rem",
                    fontWeight: 600,
                    color: "#0366d6",
                  }}
                >
                  {stats?.company.totalVehicles || 0}
                </Typography>
              </Box>
              {Object.keys(stats?.vehicleStatus).map((key: any) => (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 0.5,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "0.75rem", color: "#586069" }}
                  >
                    {key}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 500,
                    }}
                  >
                    {stats?.vehicleStatus[key as any] || 0}
                  </Typography>
                </Box>
              ))}
            </Paper>
          </Grid>

          {/* Staff Overview Card */}
          <Grid item xs={12}>
            <Paper
              sx={{
                p: 1.5,
                border: "1px solid #e1e4e8",
                borderRadius: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  mb: 1,
                  color: "#24292e",
                }}
              >
                Staff Overview
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 0.8,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.75rem",
                    color: "#586069",
                    fontWeight: 500,
                  }}
                >
                  Total Staff
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: "1rem",
                    fontWeight: 600,
                    color: "#0366d6",
                  }}
                >
                  {stats?.company.totalStaff || 0}
                </Typography>
              </Box>
              {Object.keys(stats?.staffStatus).map((key: any) => (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 0.5,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "0.75rem", color: "#586069" }}
                  >
                    {formatText(key)}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 500,
                    }}
                  >
                    {stats?.staffStatus[key as any] || 0}
                  </Typography>
                </Box>
              ))}
            </Paper>
          </Grid>

          {/* Contract Summary Card */}
          <Grid item xs={12}>
            <Paper
              sx={{
                p: 1.5,
                border: "1px solid #e1e4e8",
                borderRadius: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  mb: 1,
                  color: "#24292e",
                }}
              >
                Contract Summary
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 0.8,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontSize: "0.75rem", color: "#586069" }}
                >
                  Contracts
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: "1rem",
                    fontWeight: 600,
                    color: "#0366d6",
                  }}
                >
                  {superProjectStats?.contractSummary.totalSuperProjectsCount ||
                    0}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontSize: "0.75rem", color: "#586069" }}
                >
                  Divisions
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: "0.85rem",
                    fontWeight: 500,
                    color: "#24292e",
                  }}
                >
                  {superProjectStats?.contractSummary.totalProjectsCount || 0}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
        {/* for fuel consumption dashboard */}
        <Grid container item md={9} xs={12} spacing={2} sx={{ height: "100%" }}>
          {/* Filter and Date Range */}
          <Grid item xs={12}>
            {renderFilterAndDateRange()}
          </Grid>
          {/* Fuel Summary Cards */}
          <Grid item xs={12}>
            {renderFuelSummaryCards()}
          </Grid>

          {/* Top Fuel Stations Chart */}
          <Grid item xs={12} md={6}>
            {renderTopFuelStationsChart()}
          </Grid>

          {/* Vehicle-wise Consumption Chart */}
          <Grid item xs={12} md={6}>
            {renderVehicleWiseConsumption()}
          </Grid>
        </Grid>
      </Grid>
    </Container>
  );
};

export default LargeFleetDashboard;
